import { BLOG_CFG } from './blogConfig';
import { CAMPAIGN_TOOLS } from './campaign';
import { PILLARS, pillarFor } from './editorial';
import { WEIRD_T, DISCORD_T, BIO_T } from './generatorsI18n';

// This module is also loaded by scripts/prerender.mjs at build time, so it
// must stay plain JavaScript: no JSX, no CSS or image imports.
export const SITE_URL = 'https://cursivefont.org';

const TITLES = {
  '/': 'Cursive Text Generator — Copy & Paste | Cursivefont',
  '/weird-text-generator': WEIRD_T.en.docTitle,
  '/discord-font-generator': DISCORD_T.en.docTitle,
  '/instagram-bio-generator': BIO_T.en.docTitle,
  '/blogs': BLOG_CFG.en.title,
  '/about-us': 'About us - Cursive Text Generator',
  '/contact-us': 'Contact us - Cursive Text Generator',
  '/fr/ecriture-cursive': 'Écriture cursive à copier-coller | Cursivefont',
  '/fr/texte-bizarre': 'Texte bizarre : lettres glitch à copier | Cursivefont',
  '/fr/polices-discord': 'Polices Discord : pseudos à copier | Cursivefont',
  '/fr/generateur-bio-instagram': BIO_T.fr.docTitle,
  '/fr/blog': BLOG_CFG.fr.title,
  '/fr/a-propos': 'À propos - Écriture Cursive',
  '/fr/contact': 'Contact - Écriture Cursive',
  '/es/generador-de-texto-cursivo': 'Letras cursivas para copiar y pegar | Cursivefont',
  '/id/tulisan-keren': 'Tulisan keren: salin dan tempel | Cursivefont',
};

// Pages available in several languages. Blog posts are written separately
// for each language, so they are not translations and stay out of this list.
const TRANSLATIONS = [
  { en: '/', fr: '/fr/ecriture-cursive', es: '/es/generador-de-texto-cursivo', id: '/id/tulisan-keren' },
  { en: '/weird-text-generator', fr: '/fr/texte-bizarre' },
  { en: '/discord-font-generator', fr: '/fr/polices-discord' },
  { en: '/instagram-bio-generator', fr: '/fr/generateur-bio-instagram' },
  { en: '/privacy', fr: '/fr/confidentialite' },
  { en: '/about-us', fr: '/fr/a-propos' },
  { en: '/contact-us', fr: '/fr/contact' },
];

const NOT_FOUND = {
  en: {
    lang: 'en',
    title: 'Page not found - Cursive Text Generator',
    description: 'This page does not exist. Head back to the free cursive text generator to create stylish Unicode text.',
  },
  fr: {
    lang: 'fr',
    title: 'Page introuvable - Écriture Cursive',
    description: 'Cette page n’existe pas. Retournez au générateur d’écriture cursive pour créer vos textes en lettres Unicode.',
  },
};

// Keep descriptions specific to the page visitors are about to open.
export const PAGE_METADATA = {
  '/': {
    lang: 'en',
    description: 'Create cursive text to copy and paste into Instagram, TikTok and Discord. Try 18 free Unicode styles and export your text without signing up.',
  },
  '/weird-text-generator': {
    lang: 'en',
    description: 'Turn ordinary text into weird Unicode lettering. Create glitch, flipped and decorative text, then copy and paste it into bios, chats and social posts.',
  },
  '/discord-font-generator': {
    lang: 'en',
    description: 'Create stylish Discord text for nicknames, messages and server channels. Preview Unicode fonts, copy your favorite style and paste it into Discord.',
  },
  '/instagram-bio-generator': {
    lang: 'en',
    description: 'Build an Instagram bio with stylish fonts, symbols and ready-to-edit templates. Preview your profile, then copy your finished bio for free.',
  },
  '/blogs': {
    lang: 'en',
    description: 'Explore guides to cursive text, Unicode fonts and creative lettering. Find ideas for Instagram profiles, wedding invitations and everyday messages.',
  },
  '/about-us': {
    lang: 'en',
    description: 'Learn how Cursivefont’s free browser-based tools transform text into Unicode lettering, and how to report a question or a display issue.',
  },
  '/contact-us': {
    lang: 'en',
    description: 'Contact the Cursive Text Generator team with a question, bug report or suggestion. Help us improve our free text tools, styles and language support.',
  },
  '/fr/ecriture-cursive': {
    lang: 'fr',
    description: 'Transformez votre texte en écriture cursive à copier-coller sur Instagram, TikTok et Discord. Essayez 18 styles Unicode gratuits, sans inscription.',
  },
  '/fr/texte-bizarre': {
    lang: 'fr',
    description: 'Créez du texte bizarre avec des lettres Unicode, des effets glitch et des caractères retournés. Copiez-collez vos créations dans vos bios et messages.',
  },
  '/fr/polices-discord': {
    lang: 'fr',
    description: 'Personnalisez vos pseudos, messages et salons Discord avec des polices Unicode originales. Prévisualisez votre texte et copiez votre style préféré.',
  },
  '/fr/generateur-bio-instagram': {
    lang: 'fr',
    description: 'Créez une bio Instagram avec des polices originales, des symboles et des modèles personnalisables. Prévisualisez votre profil et copiez votre bio gratuitement.',
  },
  '/fr/blog': {
    lang: 'fr',
    description: 'Guides pratiques sur les écritures stylées, les bios Instagram, les pseudos et les caractères Unicode : exemples, limites et outils à copier-coller.',
  },
  '/fr/a-propos': {
    lang: 'fr',
    description: 'Découvrez Cursivefont, ses outils Unicode gratuits, le traitement local de vos textes et les guides pour vérifier vos écritures stylisées.',
  },
  '/fr/contact': {
    lang: 'fr',
    description: 'Contactez l’équipe Écriture Cursive pour poser une question, signaler un problème ou proposer un style. Aidez-nous à améliorer nos générateurs gratuits.',
  },
  '/es/generador-de-texto-cursivo': {
    lang: 'es',
    description: 'Convierte tu texto en letras cursivas para copiar y pegar en Instagram, TikTok y Discord. Prueba 18 estilos Unicode gratis, sin registro ni instalación.',
  },
  '/id/tulisan-keren': {
    lang: 'id',
    description: 'Ubah teks menjadi tulisan keren untuk disalin ke Instagram, TikTok dan Discord. Coba 18 gaya Unicode gratis dan ekspor teks tanpa perlu mendaftar.',
  },
};

PAGE_METADATA['/privacy'] = { lang: 'en', title: 'Privacy and local preferences | Cursivefont', description: 'Learn how Cursivefont handles your text, local preferences and email contact. Review what stays in your browser and change your privacy choices.' };
PAGE_METADATA['/fr/confidentialite'] = { lang: 'fr', title: 'Confidentialité et préférences | Cursivefont', description: 'Découvrez comment Cursivefont traite vos textes, vos préférences locales et vos contacts par e-mail, et comment modifier vos choix.' };
for (const pillar of PILLARS) {
  PAGE_METADATA[pillar.path] = { lang: 'fr', title: pillar.metaTitle, description: pillar.description, image: `${SITE_URL}/images/blog/typography.png`, pillar };
}

// Article excerpts remain the source of truth when editorial copy changes.
for (const [path, title] of Object.entries(TITLES)) {
  PAGE_METADATA[path].title = title;
}

const LEGACY_TITLES = {
  "playful-vs-elegant-cursive-fonts": "Choosing a Cursive Style for Posts | Cursivefont",
  "free-cursive-font-tools-vs-paid": "Free Cursive Tools or Paid Fonts? | Cursivefont",
  "how-to-style-your-wedding-invitations": "Cursive Wedding Invitation Ideas | Cursivefont",
  "cursive-font-copy-and-paste": "Unicode Cursive vs Installed Fonts | Cursivefont",
  "uses-of-a-cursive-writing-generator": "10 Uses for Cursive Text | Cursivefont",
  "write-your-name-in-cursive-on-instagram": "A Cursive Name on Instagram: Steps | Cursivefont",
  "cursive-font-generator": "How a Cursive Text Generator Works | Cursivefont",
  "ecrire-son-prenom-en-cursive-sur-instagram": "Un prénom cursif sur Instagram | Cursivefont",
  "cursive-ou-police-installee": "Cursive Unicode ou police installée ? | Cursivefont",
  "faire-part-de-mariage-en-ecriture-cursive": "Faire-part : 5 idées en cursive | Cursivefont",
  "alphabet-cursif-de-a-a-z": "Alphabet cursif Unicode de A à Z | Cursivefont",
  "idees-pseudo-discord-et-tiktok": "5 idées de pseudos Discord et TikTok | Cursivefont",
  "pourquoi-certaines-lettres-s-affichent-en-carres": "Lettres en carrés : causes et solutions | Cursivefont"
};

// Explicit metadata overrides for legacy excerpts; do not pad visible articles.
const LEGACY_DESCRIPTIONS = {
  'cursive-font-generator': 'See how a cursive text generator converts ordinary letters into Unicode characters. Compare styles, copy a sample and check its display in your app.',
  'how-to-style-your-wedding-invitations': 'Explore cursive ideas for digital wedding invitations, RSVP messages and save-the-dates. Choose readable text and check the final result before sharing.',
  'uses-of-a-cursive-writing-generator': 'Explore ten ways to use cursive text in short messages, profiles and gift tags. Compare Unicode lettering with real fonts for longer or printed designs.',
  'write-your-name-in-cursive-on-instagram': 'Write your displayed Instagram name in cursive without installing an app. Follow the copy-and-paste steps, then check accents and readability on mobile.',
  'ecrire-son-prenom-en-cursive-sur-instagram': 'Personnalisez votre prénom dans le nom affiché Instagram avec une écriture cursive. Vérifiez les accents et la lisibilité sur mobile après le collage.',
  'pourquoi-certaines-lettres-s-affichent-en-carres': 'Des carrés remplacent vos lettres stylées ? Comprenez le rôle des polices et testez une version plus simple dans votre application avant de partager.',
};

for (const [lang, config] of Object.entries(BLOG_CFG)) {
  const site = lang === 'fr' ? 'Écriture Cursive' : 'Cursive Text Generator';
  for (const post of config.posts) {
    PAGE_METADATA[`${config.base}/${post.slug}`] = {
      lang,
      title: post.metaTitle || LEGACY_TITLES[post.slug] || `${post.title} - ${site}`,
      post,
      noindex: post.noindex === true,
      description: post.metaDescription || LEGACY_DESCRIPTIONS[post.slug] || post.excerpt,
      image: `${SITE_URL}${post.img}`,
    };
  }
}

for (const tool of CAMPAIGN_TOOLS) {
  PAGE_METADATA[tool.path] = { lang: 'en', title: tool.metaTitle, description: tool.description, noindex: tool.noindex, tool };
}

export const ROUTES = Object.keys(PAGE_METADATA);

const alternatesOf = (path) => {
  const group = TRANSLATIONS.find((g) => Object.values(g).includes(path));
  if (!group) return [];
  return [
    ...Object.entries(group).map(([hreflang, p]) => ({ hreflang, href: `${SITE_URL}${p}` })),
    { hreflang: 'x-default', href: `${SITE_URL}${group.en}` },
  ];
};

export function getPageMetadata(pathname) {
  const path = pathname.replace(/\/+$/, '').toLowerCase() || '/';
  const page = PAGE_METADATA[path];
  if (!page) {
    return { ...NOT_FOUND[path.startsWith('/fr/') ? 'fr' : 'en'], path, canonical: null, alternates: [], image: null, noindex: true };
  }
  const filename = path === '/' ? 'home' : path.slice(1).replace(/\//g, '--');
  const meta = {
    noindex: false, ...page, path, canonical: `${SITE_URL}${path}`, alternates: alternatesOf(path),
    image: `${SITE_URL}/images/social/${filename}.png`,
    imageWidth: 1200,
    imageHeight: 630,
    imageType: 'image/png',
    imageAlt: `Cursivefont — ${page.title.replace(/\s*(?:\| Cursivefont| - Cursive Text Generator| - Écriture Cursive)$/, '')}`,
  };
  return { ...meta, structuredData: structuredDataFor(meta) };
}

function structuredDataFor(meta) {
  const organization = { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Cursivefont', url: SITE_URL };
  const graph = [organization, { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, name: 'Cursivefont', url: SITE_URL, inLanguage: ['en', 'fr', 'es', 'id'], publisher: { '@id': organization['@id'] } }];
  const page = { '@type': 'WebPage', '@id': `${meta.canonical}#webpage`, url: meta.canonical, name: meta.title, description: meta.description, inLanguage: meta.lang, isPartOf: { '@id': `${SITE_URL}/#website` } };
  graph.push(page);
  // An in-page figure (infographic) becomes the page's primary image for search and Pinterest.
  const figure = (meta.post || meta.pillar)?.body?.find(block => block.type === 'figure');
  if (figure) {
    const imageId = `${meta.canonical}#primaryimage`;
    page.primaryImageOfPage = { '@id': imageId };
    graph.push({ '@type': 'ImageObject', '@id': imageId, url: `${SITE_URL}${figure.src}`, contentUrl: `${SITE_URL}${figure.src}`, width: figure.width, height: figure.height, encodingFormat: figure.src.endsWith('.png') ? 'image/png' : 'image/jpeg', caption: figure.caption, description: figure.alt, inLanguage: meta.lang, creditText: 'Cursivefont', creator: { '@id': organization['@id'] }, copyrightHolder: { '@id': organization['@id'] } });
  }
  const home = meta.lang === 'fr' ? '/fr/ecriture-cursive' : '/';
  const crumbs = [{ name: 'Cursivefont', path: home }];
  const pillar = meta.post && pillarFor(meta.post.cluster);
  if (pillar) crumbs.push({ name: pillar.title, path: pillar.path });
  if (meta.path !== home) crumbs.push({ name: meta.post?.title || meta.pillar?.title || meta.title, path: meta.path });
  if (crumbs.length > 1) graph.push({ '@type': 'BreadcrumbList', itemListElement: crumbs.map((crumb, i) => ({ '@type': 'ListItem', position: i + 1, name: crumb.name, item: `${SITE_URL}${crumb.path}` })) });
  if (meta.post) {
    const post = meta.post;
    graph.push({ '@type': 'BlogPosting', '@id': `${meta.canonical}#article`, headline: post.title, description: post.excerpt, image: [meta.image], inLanguage: meta.lang, mainEntityOfPage: { '@id': page['@id'] }, author: { '@id': organization['@id'] }, publisher: { '@id': organization['@id'] }, ...(post.preparedAt ? { dateModified: post.preparedAt } : {}), ...(post.publishedAt ? { datePublished: post.publishedAt } : {}) });
  } else if (meta.tool || meta.pillar || ['/', '/fr/ecriture-cursive', '/es/generador-de-texto-cursivo', '/id/tulisan-keren', '/weird-text-generator', '/discord-font-generator', '/instagram-bio-generator', '/fr/texte-bizarre', '/fr/polices-discord', '/fr/generateur-bio-instagram'].includes(meta.path)) {
    graph.push({ '@type': 'WebApplication', name: meta.pillar?.title || meta.title, url: meta.canonical, applicationCategory: 'UtilitiesApplication', operatingSystem: 'Any', browserRequirements: 'Requires JavaScript for text conversion', isAccessibleForFree: true, offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' } });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}
